import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellRing, Plus, Trash2, Pill, CalendarClock, NotebookPen } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { pushSupported, subscribePush } from "../lib/push";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SecureAccountCard } from "../components/SecureAccountCard";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const KINDS = [
  { v: "dose", l: "Dose time", icon: Pill },
  { v: "taper_step", l: "Taper step day", icon: CalendarClock },
  { v: "journal", l: "Journal / reflection", icon: NotebookPen },
];
const KIND_ICON = Object.fromEntries(KINDS.map((k) => [k.v, k.icon]));

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [kind, setKind] = useState("dose");
  const [time, setTime] = useState("09:00");
  const [label, setLabel] = useState("");
  const [days, setDays] = useState([...DAYS]);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [prefs, setPrefs] = useState(null);

  const load = () => api.get("/reminders").then(({ data }) => setReminders(data.reminders)).catch(() => {});
  useEffect(() => { load(); api.get("/notifications/preferences").then(({ data }) => setPrefs(data)).catch(() => {}); }, []);

  const savePrefs = async (next) => {
    setPrefs(next);
    try { await api.patch("/notifications/preferences", next); } catch (e) { toast.error(apiErr(e)); }
  };

  const enablePush = async () => {
    if (!pushSupported()) { toast.error("Your browser doesn't support notifications."); return; }
    try {
      const { granted } = await subscribePush();
      setPerm(granted ? "granted" : Notification.permission);
      if (granted) { toast.success("Notifications enabled — reminders will reach you even when the tab is closed."); }
      else toast.info("Notifications stay off — you'll still see in-app reminders.");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const sendTest = async () => {
    try { await api.post("/push/test"); toast.success("Test notification sent."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const toggleDay = (d) => setDays((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d]);

  const add = async () => {
    if (days.length === 0) { toast.error("Choose at least one day."); return; }
    try {
      await api.post("/reminders", { kind, time, label, days, enabled: true });
      toast.success("Reminder added.");
      setLabel(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggle = async (r) => {
    try { await api.patch(`/reminders/${r.id}`, { ...r, enabled: !r.enabled }); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/reminders/${id}`); toast.success("Reminder removed."); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-8">
      <SecureAccountCard />
      <div>
        <h1 className="font-head text-3xl font-bold text-[#2C363F] sm:text-4xl">Reminders</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">Gentle nudges for dose times, taper steps and journalling. You're in control — turn any of them on or off whenever you like.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-white p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-full ${perm === "granted" ? "bg-[#4A7c59]/10 text-[#4A7c59]" : "bg-muted text-muted-foreground"}`}>{perm === "granted" ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}</div>
          <div>
            <p className="font-head font-semibold text-[#2C363F]">Browser notifications</p>
            <p className="text-sm text-muted-foreground">{perm === "granted" ? "On — reminders reach you even when TaperPath is closed." : "Off — you'll still see in-app reminders."}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {perm === "granted" && <Button data-testid="test-push" onClick={sendTest} variant="outline" className="rounded-full">Send test</Button>}
          {perm !== "granted" && <Button data-testid="enable-push" onClick={enablePush} className="rounded-full bg-[#4A7c59] hover:bg-[#3B6347]">Enable notifications</Button>}
        </div>
      </div>

      {prefs && (
        <div className="rounded-2xl border border-border bg-white p-6 shadow-sm" data-testid="notif-prefs">
          <h3 className="font-head text-lg font-semibold text-[#2C363F]">Notification preferences</h3>
          <p className="mt-1 text-sm text-muted-foreground">Choose how and when TaperPath reaches you. You're always in control.</p>
          <div className="mt-4 divide-y divide-border">
            <div className="flex items-center justify-between py-3">
              <div><p className="font-medium text-[#2C363F]">Push notifications</p><p className="text-sm text-muted-foreground">Reminders & buddy alerts on your device.</p></div>
              <Switch data-testid="pref-push" checked={prefs.push} onCheckedChange={(v) => savePrefs({ ...prefs, push: v })} />
            </div>
            <div className="flex items-center justify-between py-3">
              <div><p className="font-medium text-[#2C363F]">Email notifications</p><p className="text-sm text-muted-foreground">Buddy check-in alerts by email (if your account has one).</p></div>
              <Switch data-testid="pref-email" checked={prefs.email} onCheckedChange={(v) => savePrefs({ ...prefs, email: v })} />
            </div>
            <div className="py-3">
              <div className="flex items-center justify-between">
                <div><p className="font-medium text-[#2C363F]">Quiet hours</p><p className="text-sm text-muted-foreground">Pause push notifications overnight.</p></div>
                <Switch data-testid="pref-quiet" checked={prefs.quiet_enabled} onCheckedChange={(v) => savePrefs({ ...prefs, quiet_enabled: v })} />
              </div>
              {prefs.quiet_enabled && (
                <div className="mt-3 flex items-center gap-3">
                  <div><Label className="text-xs">From</Label><Input data-testid="quiet-start" type="time" value={prefs.quiet_start} onChange={(e) => savePrefs({ ...prefs, quiet_start: e.target.value })} className="mt-1 rounded-xl" /></div>
                  <div><Label className="text-xs">To</Label><Input data-testid="quiet-end" type="time" value={prefs.quiet_end} onChange={(e) => savePrefs({ ...prefs, quiet_end: e.target.value })} className="mt-1 rounded-xl" /></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
        <h3 className="font-head text-lg font-semibold text-[#2C363F]">Add a reminder</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <Label>Type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="reminder-kind" className="mt-1 rounded-xl py-6"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Time</Label><Input data-testid="reminder-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 rounded-xl py-6" /></div>
          <div><Label>Label (optional)</Label><Input data-testid="reminder-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Morning diazepam" className="mt-1 rounded-xl py-6" /></div>
        </div>
        <div className="mt-4">
          <Label>Days</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {DAYS.map((d) => (
              <button key={d} onClick={() => toggleDay(d)} data-testid={`day-${d}`} className={`h-10 w-14 rounded-full text-sm font-medium transition-colors duration-200 ${days.includes(d) ? "bg-[#4A7c59] text-white" : "bg-muted text-muted-foreground"}`}>{d}</button>
            ))}
          </div>
        </div>
        <Button data-testid="add-reminder" onClick={add} className="mt-5 rounded-full bg-[#4A7c59] py-6 hover:bg-[#3B6347]"><Plus className="mr-2 h-4 w-4" /> Add reminder</Button>
      </div>

      <div className="space-y-3" data-testid="reminders-list">
        {reminders.map((r) => {
          const Icon = KIND_ICON[r.kind] || Bell;
          return (
            <div key={r.id} className="flex items-center justify-between rounded-2xl border border-border bg-white p-5 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#8FB8DE]/15 text-[#2C4763]"><Icon strokeWidth={1.8} className="h-5 w-5" /></div>
                <div>
                  <p className="font-head font-semibold text-[#2C363F]">{r.label || KINDS.find((k) => k.v === r.kind)?.l} · {r.time}</p>
                  <p className="text-xs text-muted-foreground">{(r.days || []).join(", ")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} data-testid={`toggle-${r.id}`} />
                <button onClick={() => remove(r.id)} data-testid={`del-reminder-${r.id}`} className="text-muted-foreground hover:text-[#8a4436]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
        {reminders.length === 0 && <p className="py-10 text-center text-muted-foreground">No reminders yet. Add one above to get gentle nudges.</p>}
      </div>
    </div>
  );
}
